import { useEffect, useState } from "react";

export type TimerPillProps = {
  total: number;
  active: boolean;
  onExpire: () => void;
};

/**
 * Editorial timer — Source-Serif "00:60" with a thin Ember underline that
 * retracts left-to-right as time elapses.
 */
export function TimerPill({ total, active, onExpire }: TimerPillProps) {
  const [remaining, setRemaining] = useState(total);

  useEffect(() => {
    setRemaining(total);
  }, [total]);

  useEffect(() => {
    if (!active) return;
    if (remaining <= 0) {
      onExpire();
      return;
    }
    const t = setTimeout(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [active, remaining, onExpire]);

  const danger = remaining <= 10;
  const mm = Math.floor(remaining / 60).toString().padStart(2, "0");
  const ss = (remaining % 60).toString().padStart(2, "0");
  const color = danger ? "var(--fail)" : "var(--ink-muted)";

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--panel-soft)] px-3 py-1.5 leading-none"
      role="timer"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`${remaining} seconden over om te antwoorden`}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden
        style={{ color, transition: "color 200ms ease" }}
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span
        className="font-mono-badge text-[13px] font-semibold tabular-nums"
        style={{ color, transition: "color 200ms ease" }}
        aria-hidden="true"
      >
        {mm}:{ss}
      </span>
    </span>
  );
}
